import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Typography,
} from "@suraasa/placebo-ui"
import { ToolType } from "api/resources/aiTools/types"
import clsx from "clsx"
import ErrorPage from "components/ErrorPage"
import PageContainer from "components/PageContainer"
import TabBar from "components/TabBar"
import useTabs, { Tabs } from "components/TabBar/hooks/useTabs"
import TopHeadingBar from "components/TopHeadingBar"
import AIToolsOnboarding from "features/Home/SuraasaPlusNewYearOnboarding/AIToolsOnboarding"
import { AI_TOOLS_ONBOARDING_TARGETS } from "features/Home/SuraasaPlusNewYearOnboarding/AIToolsOnboarding/types"
import { useSuraasaPlusNewYear } from "features/Home/SuraasaPlusNewYearOnboarding/Context"
import GenerationsAvailable from "features/SuraasaPlus/components/GenerationAvailable"
import FreeTrialBanner from "features/SuraasaPlus/FreeTrial/Banner"
import GenerationLimitReachedCard from "features/SuraasaPlus/GenerationLimitReached/Card"
import { useFeatureToggle } from "global/FeatureToggleProvider"
import { useNavigate } from "react-router-dom"

import styles from "./AItools.module.css"
import Card from "./components/Card"
import History from "./components/History"
import {
  allTools,
  communicationAndDocumentationTools,
  contentCreationTools,
  contentSummarizationTools,
  getToolName,
  gradingAndAssessmentTools,
} from "./helper"

const BuildTools = ({
  heading,
  subHeading,
  tools,
}: {
  heading: string
  subHeading: string
  tools: {
    toolType: ToolType
    title: string
    description: string
    to: string
    gradient: string
  }[]
}) => {
  if (tools.length === 0) return
  return (
    <div className="mb-5">
      <div className="flex max-w-[3/4] flex-col justify-start gap-0.5 px-2 md:flex-row md:gap-1 ">
        <Typography variant="title2" className="!text-[24px] !font-semibold">
          {heading}
        </Typography>
        <Typography
          variant="title2"
          className="!text-[24px] !font-semibold text-onSurface-400"
        >
          {subHeading}
        </Typography>
      </div>

      <div className="scrollbar-hide mt-2 flex flex-row gap-1.5 overflow-x-auto px-2 md:w-full">
        {tools.map(i => (
          <Card {...i} key={i.title} className="basis-1/4" />
        ))}
      </div>
    </div>
  )
}

export enum AIToolsTabs {
  tools = "Tools",
  history = "History",
}

const AItools = () => {
  const navigate = useNavigate()
  const { isSpnyPlatformIntroCompleted } = useSuraasaPlusNewYear()
  const featureToggle = useFeatureToggle()
  const isAIToolsEnabled = featureToggle.aiTools.isEnabled
  const inactiveTools = featureToggle.aiTools.inactiveTools || []

  const tabs: Tabs = [
    {
      name: AIToolsTabs.tools,
      content: () => null,
    },
    {
      name: AIToolsTabs.history,
      content: () => null,
    },
  ]

  const [activeTab, setActiveTab] = useTabs({
    tabs,
    initialTab: tabs[0].name,
  })

  if (!isAIToolsEnabled) {
    return <ErrorPage errorStatus={404} />
  }

  const getActiveTools = ({
    tools,
  }: {
    tools: {
      toolType: ToolType
      title: string
      description: string
      to: string
      gradient: string
    }[]
  }) => {
    return tools.filter(i => !inactiveTools.includes(i.toolType))
  }

  return (
    <div className={clsx(styles.pageContainer, "flex flex-col bg-[#FCFCFC]")}>
      <FreeTrialBanner />
      <TopHeadingBar
        heading={<span>AI Tools</span>}
        renderBorder
        endSlot={
          <div className="flex flex-row items-center gap-3">
            <div className="hidden md:block">
              <GenerationsAvailable />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button
                  variant="filled"
                  color="black"
                  size="sm"
                  className="py-[7px]"
                >
                  Generate New
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuGroup>
                  {getActiveTools({ tools: allTools }).map(x => (
                    <DropdownMenuItem
                      key={x.toolType}
                      onClick={() => {
                        navigate(x.to)
                      }}
                    >
                      {getToolName(x.toolType)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      >
        <TabBar
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
          getOnboardingTarget={tab => {
            if (tab.name === AIToolsTabs.tools)
              return AI_TOOLS_ONBOARDING_TARGETS.exploreTools

            if (tab.name === AIToolsTabs.history)
              return AI_TOOLS_ONBOARDING_TARGETS.accessHistory
          }}
        />
      </TopHeadingBar>

      {activeTab.name === "Tools" && (
        <PageContainer className="mb-6 mt-2.5 max-md:px-0 md:mt-2">
          <div className="mx-2 mb-4.5 block md:mx-0 md:hidden">
            <GenerationsAvailable />
          </div>
          <GenerationLimitReachedCard />
          <BuildTools
            heading="Content Creation."
            subHeading="Effortlessly Create. Teach More."
            tools={getActiveTools({ tools: contentCreationTools })}
          />
          <BuildTools
            heading="Grading & Assessment."
            subHeading="Assess. Grade. Simplified."
            tools={getActiveTools({ tools: gradingAndAssessmentTools })}
          />
          <BuildTools
            heading="Content Summarization & Enhancement."
            subHeading="Summarize. Enhance. Perfect."
            tools={getActiveTools({ tools: contentSummarizationTools })}
          />
          <BuildTools
            heading="Communication & Documentation."
            subHeading="Polished Communication. Every Time."
            tools={getActiveTools({
              tools: communicationAndDocumentationTools,
            })}
          />
        </PageContainer>
      )}
      {activeTab.name === "History" && (
        <PageContainer className="mb-6 mt-2">
          <History />
        </PageContainer>
      )}

      {isSpnyPlatformIntroCompleted && (
        <AIToolsOnboarding navigate={navigate} setActiveTab={setActiveTab} />
      )}

      {/* <div className="hidden md:block">
          <HomeSidebar />
        </div> */}
    </div>
  )
}

export default AItools
